<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('customer.customers') }}
        </span>
        <h3 class="page-title">
          {{ $t('customer.addingNewCustomer') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/klienci')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('customer.addCustomer') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="customer-new-form" tag="form">
        <d-card class="mb-5">
          <d-tabs
            card
            v-if="!loadError"
            :nav-class="allDataLoaded ? validationResults : ''">
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

             <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('users.customer') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('settings.country.country') }}:
                    </label>

                    <d-form-select
                      v-model="countryID"
                      :options="countriesOptions"
                      @change="countryChange" />
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.language') }}:
                    </label>

                    <div :class="{ 'form-control': true, 'is-select': true }">
                      <v-select
                        :options="languageOptions"
                        v-model="language_id"
                        :searchable="false"
                        :clearable="false"
                        :reduce="language => language.value"
                        :placeholder="$t('settings.language.chooseLanguage')"
                        label="text">
                        <template slot="no-options">
                          {{ $t('settings.language.noLanguagesAvailable') }}&hellip;
                        </template>
                      </v-select>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label>
                  {{ $t('customer.customerLogo') }}:
                </label>

                <file-input
                  :onChange="setCustomerLogo"
                  id="inputFileCustomerLogo" />
              </div>

              <d-row>
                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.partnerDiscountProducts') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|max_value:100|only-numbers"
                      name="partner_discount_products"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <input
                          v-model="partnerDiscountProducts"
                          type="text"
                          class="text-right form-control"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.partnerDiscountServices') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|max_value:100|only-numbers"
                      name="partner_discount_services"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <input
                          v-model="partnerDiscountServices"
                          type="text"
                          class="text-right form-control"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.partnerDiscountShipping') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|max_value:100|only-numbers"
                      name="partner_discount_shipping"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <input
                          v-model="partnerDiscountShipping"
                          type="text"
                          class="text-right form-control"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.emailAddress') }}:
                    </label>

                    <validation-provider
                      rules="email"
                      name="email"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        type="email"
                        v-model="email" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.telephone') }}:
                    </label>

                    <d-input
                      v-mask="'+## ### ### ### ### ### ### ### ### ###'"
                      v-model="phone" />
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label>
                  {{ $t('customer.termsOfSale') }}:
                </label>

                <d-textarea
                  v-model="termsOfSale"
                  :value.prop="termsOfSale" />
              </div>
            </d-tab>

            <d-tab :title="$t('customer.correspondenceInfo')">
              <div class="form-group">
                <label>
                  {{ $t('settings.country.country') }}:
                </label>

                <d-form-select
                  v-model="correspondenceCountryID"
                  :options="countriesOptions" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.nameAndSurename') }}:
                </label>

                <d-input
                  v-model="correspondenceName" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.companyName') }}:
                </label>

                <d-input
                  v-model="correspondenceCompanyName" />
              </div>

              <d-row>
                <d-col cols="12" md="10" lg="10">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.city') }}:
                    </label>

                    <d-input
                      v-model="correspondenceCity" />
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.zipCode') }}:
                    </label>

                    <d-input
                      v-model="correspondencePostcode" />
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="12" md="8" lg="8">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.street') }}:
                    </label>

                    <d-input
                      v-model="correspondenceStreet" />
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.buildingNumber') }}:
                    </label>

                    <d-input v-model="correspondenceBuilding" />
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.houseNumber') }}:
                    </label>

                    <d-input v-model="correspondenceLocale" />
                  </div>
                </d-col>
              </d-row>
            </d-tab>

            <d-tab :title="$t('customer.invoiceInfo')">
              <d-row>
                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('settings.country.countryCode') }}:
                    </label>

                    <d-form-select
                      v-model="invoiceCountryPrefix"
                      :options="countriesCodesOptions" />
                  </div>
                </d-col>

                <d-col cols="12" md="10" lg="10">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.vatIDNumber') }}:
                    </label>

                    <div class="d-flex">
                      <d-input
                        v-model="invoiceTaxNumber" />

                      <d-button
                        v-if="invoiceCountryPrefix && invoiceTaxNumber && nonVerifiedCountryPrefixes.indexOf(invoiceCountryPrefix) === -1"
                        theme="success"
                        :disabled="checkingTaxNumber"
                        class="ml-2 w-25"
                        @click.prevent="verifyCustomerTaxID">
                        <div
                          v-if="checkingTaxNumber"
                          class="spinner-border spinner-border-sm"
                          role="status"></div>
                        <template v-if="invoiceCountryPrefix === 'PL'">{{ $t('customer.getDataFromGUS') }}</template>
                        <template v-else>{{ $t('customer.getDataFromVIES') }}</template>
                      </d-button>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="12">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.invoiceName') }}
                    </label>

                    <d-input
                      v-model="invoiceName" />
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="12" md="10" lg="10">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.city') }}:
                    </label>

                    <d-input
                      v-model="invoiceCity" />
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.zipCode') }}:
                    </label>

                    <d-input
                      v-model="invoicePostcode" />
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="12" md="8" lg="8">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.street') }}:
                    </label>

                    <d-input
                      v-model="invoiceStreet" />
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.buildingNumber') }}:
                    </label>

                    <d-input v-model="invoiceBuilding" />
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.houseNumber') }}:
                    </label>

                    <d-input v-model="invoiceLocale" />
                  </div>
                </d-col>
              </d-row>
            </d-tab>

            <d-tab :title="$t('customer.shippingInfo')">
              <p class="text-center">{{ $t('customer.addingShippingAddressAvailableAfterCustomerSave') }}</p>
            </d-tab>

            <d-tab :title="$t('customer.contactPeople')">
              <p class="text-center">
                {{ $t('customer.addingContactPersonAvailableAfterCustomerSave') }}
              </p>
            </d-tab>

            <d-tab :title="$t('settings.transaltion.translations')">
              <p class="text-center">
                {{ $t('customer.addingTransaltionsAvailableAfterCustomerSave') }}
              </p>
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>
  </d-container>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'customer-new',
  computed: {
    allDataLoaded () {
      return this.loadedCountries && this.loadedLanguages;
    },
    countriesOptions () {
      return [{ value: '', text: this.$t('settings.country.selectCountry') }].concat(CommonGetters.getCountriesForSelect(this.countries));
    },
    countriesCodesOptions () {
      return [{ value: '', text: this.$t('settings.country.selectCountry') }].concat(CommonGetters.getCountriesCodesForSelect(this.countries));
    },
    languageOptions () {
      return this.languages.map(language => ({
        value: language.id,
        text: language.code
      }));
    },
    validationResults () {
      let errors = this.$refs['customer-new-form'].errors;
      let errorTabs = [];
      let results = [
        ['error-1', ['name', 'email']],
        ['error-2', []]
      ];

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errors[key] && errors[key].length) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs.join(' ');
    },
    polandCountryID () {
      let countryPL = this.countries.filter(country => country.code === 'PL');

      if (countryPL[0]) {
        return countryPL[0].id;
      }

      return false;
    },
    nonVerifiedCountryIDs () {
      return this.countries.filter(country => country.verify_tax_number === 0).map(country => country.id);
    },
    nonVerifiedCountryPrefixes () {
      return this.countries.filter(country => country.verify_tax_number === 0).map(country => country.code);
    }
  },
  data() {
    return {
      name: '',
      email: '',
      phone: '',
      termsOfSale: '',
      countryID: '',
      logo: '',
      language_id: null,
      correspondenceCountryID: '',
      correspondenceName: '',
      correspondenceCompanyName: '',
      correspondenceStreet: '',
      correspondenceBuilding: '',
      correspondenceLocale: '',
      correspondencePostcode: '',
      correspondenceCity: '',
      invoiceName: '',
      invoiceEmail: '',
      invoiceStreet: '',
      invoiceBuilding: '',
      invoiceLocale: '',
      invoicePostcode: '',
      invoiceCity: '',
      invoiceTaxNumber: '',
      invoiceCountryPrefix: '',
      loadedCountries: false,
      loadedLanguages: false,
      partnerDiscountProducts: 0,
      partnerDiscountServices: 0,
      partnerDiscountShipping: 0,
      countries: [],
      languages: [],
      loadError: false,
      checkingTaxNumber: false
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    setCustomerLogo (event) {
      this.logo = event.target.files[0];
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError',
        successAction: () => {
          this.languages.reverse();
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['customer-new-form'], this.submitForm);
    },
    submitForm () {
      let imageFields = false;
      let formDataToSend = {
        name: this.name,
        email: this.email,
        phone: this.phone,
        terms_of_sale: this.termsOfSale,
        country_id: this.countryID,
        language_id: this.language_id,
        correspondence_country_id: this.correspondenceCountryID,
        correspondence_name: this.correspondenceName,
        correspondence_company_name: this.correspondenceCompanyName,
        correspondence_street: this.correspondenceStreet,
        correspondence_building: this.correspondenceBuilding,
        correspondence_locale: this.correspondenceLocale,
        correspondence_postcode: this.correspondencePostcode,
        correspondence_city: this.correspondenceCity,
        invoice_name: this.invoiceName,
        invoice_email: this.invoiceEmail,
        invoice_street: this.invoiceStreet,
        invoice_building: this.invoiceBuilding,
        invoice_locale: this.invoiceLocale,
        invoice_postcode: this.invoicePostcode,
        invoice_city: this.invoiceCity,
        invoice_tax_number: this.invoiceTaxNumber,
        invoice_country_prefix: this.invoiceCountryPrefix,
        partner_discount_products: this.partnerDiscountProducts,
        partner_discount_services: this.partnerDiscountServices,
        partner_discount_shipping: this.partnerDiscountShipping,
        region_id: '1' // TEMPORARY
      };

      if (this.logo) {
        formDataToSend.logo = this.logo;
        imageFields = ['logo'];
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customer-panel/partner-customers/store',
        formData: formDataToSend,
        successTitle: this.$t('customer.addCustomerSuccessTitle'),
        successText: this.$t('customer.addCustomerSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.addCustomerErrorText'),
        successAction: () => {
          this.$router.push('/klienci')
        }
      }, imageFields);
    },
    countryChange () {
      Vue.nextTick(() => {
        let countryData = this.countries.filter(country => country.id === this.countryID);

        if (countryData[0]) {
          let languageID = countryData[0].default_language_id;

          if (languageID) {
            Vue.set(this, 'language_id', languageID);
          } else {
            Vue.set(this, 'language_id', null);
          }
        }
      });
    },
    verifyCustomerTaxID () {
      if (!this.invoiceCountryPrefix) {
        Vue.swal({
          title: this.$t('customer.youMustSelectCountryPrefix'),
          html: this.$t('customer.youMustSelectCountryPrefixDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.checkingTaxNumber = true;

      this.$http.get('/customer-panel/partner-customers/verify-tax-number', {
        params: {
          id: 0,
          prefix: this.invoiceCountryPrefix,
          number: this.invoiceTaxNumber
        }
      }).then(response => {
        this.checkingTaxNumber = false;

        if (response.data.success) {
          if (response.data.verification && response.data.verification.status === 'VALID') {
            if (this.invoiceCountryPrefix === 'PL') {
              let previousInvoiceData = [
                this.invoiceCity,
                this.invoiceName,
                this.invoicePostcode,
                this.invoiceStreet,
                this.invoiceBuilding,
                this.invoiceLocale
              ].join('|');

              this.invoiceCity = response.data.verification.response.city;
              this.invoiceName = response.data.verification.response.name;
              this.invoicePostcode = response.data.verification.response.postCode;

              if (this.invoicePostcode && this.invoiceCountryPrefix === 'PL') {
                this.invoicePostcode = this.invoicePostcode.substr(0, 2) + '-' + this.invoicePostcode.substr(2, 3);
              }

              this.invoiceStreet = response.data.verification.response.street;

              if (this.invoiceStreet && this.invoiceStreet.substr(0, 4) === 'ul. ') {
                this.invoiceStreet = this.invoiceStreet.replace('ul. ', '');
              }

              this.invoiceBuilding = response.data.verification.response.streetNumber;
              this.invoiceLocale = response.data.verification.response.houseNumber;

              let newInvoiceData = [
                this.invoiceCity,
                this.invoiceName,
                this.invoicePostcode,
                this.invoiceStreet,
                this.invoiceBuilding,
                this.invoiceLocale
              ].join('|');

              let previousCorrespondenceData = [
                this.correspondenceCountryID,
                this.correspondenceCity,
                this.correspondenceCompanyName,
                this.correspondencePostcode,
                this.correspondenceStreet,
                this.correspondenceBuilding,
                this.correspondenceLocale
              ].join('|');

              let newCorrespondenceData = [
                parseInt(this.polandCountryID, 10),
                this.invoiceCity,
                this.invoiceName,
                this.invoicePostcode,
                this.invoiceStreet,
                this.invoiceBuilding,
                this.invoiceLocale
              ].join('|');

              if (previousCorrespondenceData !== newCorrespondenceData) {
                this.correspondenceCountryID = parseInt(this.polandCountryID, 10);
                this.correspondenceCity = this.invoiceCity;
                this.correspondenceCompanyName = this.invoiceName;
                this.correspondencePostcode = this.invoicePostcode;
                this.correspondenceStreet = this.invoiceStreet;
                this.correspondenceBuilding = this.invoiceBuilding;
                this.correspondenceLocale = this.invoiceLocale;
              }

              if (previousCorrespondenceData !== newCorrespondenceData && previousInvoiceData !== newInvoiceData) {
                Vue.swal({
                  title: this.$t('customer.invoiceAndCorrespondenceAddressSet'),
                  html: this.$t('customer.invoiceAndCorrespondenceAddressSetDesc'),
                  type: 'info',
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                });
              } else if (previousCorrespondenceData !== newCorrespondenceData) {
                Vue.swal({
                  title: this.$t('customer.correspondenceAddressSet'),
                  html: this.$t('customer.correspondenceAddressSetDesc'),
                  type: 'info',
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                });
              } else if (previousInvoiceData !== newInvoiceData) {
                Vue.swal({
                  title: this.$t('customer.invoiceAddressSet'),
                  html: this.$t('customer.invoiceAddressSetDesc'),
                  type: 'info',
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                });
              }
            }
          } else if (response.data.integration_disabled === 1) {
            Vue.swal({
              title: this.$t('customer.verificationIntegrationDisabledTitle'),
              html: this.$t('customer.verificationIntegrationDisabledDesc'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.checkingTaxNumber = false;
      });
    }
  }
};
</script>
